import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getService } from "src/helpers/isPes";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";

import options from "./defaultOptions";
import { finishedExamsKey, unfinishedExamsKey } from "./useExams";

export function useGetExam(id, enabled = true) {
	const { t } = useTranslation();
	const { questionId } = useParams();

	const query = useQuery({
		queryKey: ["exam", `${id}`],

		queryFn: async () => {
			const {
				questions,
				name,
				stats,
				status,
				type,
				startedAt,
				finishedAt,
				state,
				pausedAt,
				result,
				courseId,
				folders,
			} = await newApi.getExam(id);

			return {
				question: questions[+questionId - 1],
				questions,
				id,
				name,
				status,
				stats,
				type,
				startedAt,
				finishedAt,
				pausedAt,
				state,
				result,
				courseId,
				folders: folders || [],
			};
		},

		...options,
		enabled,
		gcTime: 60 * 1000,
	});

	// TanStack Query 5: bez onError w useQuery
	useEffect(() => {
		if (query.error) {
			toast.error(t("errors.cannotGetExam"), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 5000,
			});
		}
	}, [query.error, t]);

	return query;
}

export function useGetExamStats(examId) {
	return useQuery({
		queryKey: ["exam-stats", examId],
		queryFn: async () => newApi.getExamStats(examId),
		...options,
	});
}

export function useGetExamsStats(examsIds) {
	return useQuery({
		queryKey: ["exams-stats", examsIds],
		queryFn: async () => newApi.getExamsStats(examsIds),
		...options,
	});
}

export function setActiveQuestion(examId, questionIndex) {
	const examData = queryClient.getQueryData(["exam", `${examId}`]);
	const prevQuestionId = examData?.question?.id;
	const newQuestionId = examData?.questions[questionIndex]?.id;

	queryClient.setQueriesData({ queryKey: ["exam", `${examId}`] }, (element) => {
		return {
			...element,
			question: element.questions[questionIndex],
		};
	});

	if (prevQuestionId && prevQuestionId !== newQuestionId) {
		const service = getService();
		queryClient.removeQueries({ queryKey: ["exam-question", prevQuestionId] });
		queryClient.removeQueries({
			queryKey: [`question-${service}-stats`, prevQuestionId],
		});
		queryClient.removeQueries({
			queryKey: [`notes-${service}-${prevQuestionId}`],
		});
		queryClient.removeQueries({
			queryKey: [`discussion-${service}-${prevQuestionId}`],
		});
		queryClient.removeQueries({
			queryKey: [`articles-${service}-${prevQuestionId}`],
		});
	}
}

export function getExam(examId) {
	return queryClient.getQueryData(["exam", `${examId}`]);
}

export function useSetExcludedAnswer() {
	return useMutation({
		mutationFn: ({ examId, questionId, answer }) => {
			return newApi.setExcludedAnswer(examId, questionId, answer);
		},
		onSuccess: async (response, data) => {
			queryClient.setQueriesData(
				{ queryKey: ["exam", `${data.examId}`] },
				(details) => {
					const { questionId } = data;
					const { question } = details;
					const selectedQuestion = details.questions.find(
						(question) => question.id === questionId,
					);
					const resolvedQuestion = {
						...selectedQuestion,
						...response,
					};
					const newQuestions = details.questions.map((question) => {
						if (question.id === questionId) {
							return resolvedQuestion;
						}

						return question;
					});

					return {
						...details,
						question:
							question.id === resolvedQuestion.id ? resolvedQuestion : question,
						questions: newQuestions,
					};
				},
			);
		},
	});
}

export function useSetAnswer() {
	return useMutation({
		mutationFn: ({ examId, questionId, answer }) => {
			return newApi.setAnswer(examId, questionId, answer);
		},
		onSuccess: async (response, data) => {
			queryClient.setQueriesData(
				{ queryKey: ["exam", `${data.examId}`] },
				(details) => {
					const { questionId } = data;
					const { question } = details;
					const selectedQuestion = details.questions.find(
						(question) => question.id === questionId,
					);
					const resolvedQuestion = {
						...selectedQuestion,
						...response.answerDetails,
					};
					const newQuestions = details.questions.map((question) => {
						if (question.id === questionId) {
							return resolvedQuestion;
						}

						return question;
					});

					return {
						...details,
						isFinished: response.totalQuestions === response.resolvedQuestions,
						question:
							question.id === resolvedQuestion.id ? resolvedQuestion : question,
						questions: newQuestions,
						stats: response,
						result: response.result,
					};
				},
			);
			queryClient.removeQueries({ queryKey: ["user-stats"] });
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: ["exam-stats"] });
			queryClient.removeQueries({ queryKey: [unfinishedExamsKey] });
		},
	});
}

export function clearGetUnfinishedExamDetailsCache(id) {
	return queryClient.removeQueries({
		queryKey: [`exam-${id}-details`],
		exact: true,
	});
}

export function useGetExamQuestionDetails(id, examId, enabled = true) {
	return useQuery({
		queryKey: ["exam-question", id, examId],
		queryFn: async () => newApi.getExamQuestionDetails(id, examId),
		...options,
		enabled,
		gcTime: 60 * 1000,
	});
}

export function useGetQuestionStats(id, enabled) {
	return useQuery({
		queryKey: [`question-${getService()}-stats`, id],
		queryFn: () => newApi.getQuestionsStatistics(id),
		...options,
		enabled,
		gcTime: 60 * 1000,
	});
}

export function useToggleStar() {
	return useMutation({
		mutationFn: (id) => {
			return newApi.toggleStarred(id);
		},
		onSuccess: async (_data, id) => {
			queryClient.setQueriesData(
				{ queryKey: ["exam-question", id] },
				(element) => {
					return {
						...element,
						isStarred: !element.isStarred,
					};
				},
			);

			queryClient.setQueriesData({ queryKey: ["questions"] }, (data) => {
				const upgradedSearch = [...data.results];
				upgradedSearch.forEach((result) => {
					if (result.id === id) {
						result.details.isStarred = !result.details.isStarred;
					}
				});

				return {
					count: data.count,
					results: upgradedSearch,
				};
			});
			queryClient.setQueriesData({ queryKey: ["question", id] }, (data) => {
				return {
					...data,
					details: {
						...data.details,
						isStarred: !data.details.isStarred,
					},
				};
			});
			await queryClient.invalidateQueries({ queryKey: ["questions"] });
		},
	});
}

export function useChangeExamStatus() {
	return useMutation({
		mutationFn: async ({ id, status }) => {
			const newData = {
				...(status ? { status } : {}),
			};

			return newApi.updateExam(id, newData);
		},
		onSuccess: async (_data, params) => {
			await queryClient.invalidateQueries({ queryKey: ["exam-question"] });
			queryClient.removeQueries({ queryKey: ["exam", `${params.id}`] });
			queryClient.removeQueries({ queryKey: [unfinishedExamsKey] });
			queryClient.removeQueries({
				queryKey: [
					{
						queryKey: [`exam-${params.id}-details`],
						exact: true,
					},
				],
			});
			queryClient.removeQueries({ queryKey: ["courses"] });
			queryClient.removeQueries({ queryKey: [finishedExamsKey] });
		},
	});
}

export function usePatchExam() {
	return useMutation({
		mutationFn: async ({ id, name, status, state, type }) => {
			const newData = {
				...(name ? { name } : {}),
				...(status ? { status } : {}),
				...(state ? { state } : {}),
				...(type ? { type } : {}),
			};

			return newApi.updateExam(id, newData);
		},
		onSuccess: async (data, params) => {
			const newData = {
				...(params.name ? { name: data.name } : {}),
				...(params.status ? { status: data.status } : {}),
				...(params.type ? { type: data.type } : {}),
				state: data.state,
				pausedAt: data.pausedAt,
				finishedAt: data.finishedAt,
				createdAt: data.createdAt,
			};
			queryClient.setQueriesData(
				{ queryKey: ["exam", `${params.id}`] },
				(exam) => {
					return {
						...exam,
						...newData,
					};
				},
			);
			if (params.name) {
				queryClient.invalidateQueries({ queryKey: ["folder-items"] });
				queryClient.invalidateQueries({ queryKey: ["folder"] });
			}
			if (!newData.status) {
				queryClient.setQueriesData(
					{ queryKey: [unfinishedExamsKey] },
					(exams) => {
						return {
							...exams,
							rows: exams.rows.map((exam) => {
								if (exam.id === +params.id) {
									return {
										...exam,
										...newData,
									};
								}

								return exam;
							}),
						};
					},
				);
				queryClient.setQueriesData(
					{ queryKey: [finishedExamsKey] },
					(exams) => {
						return {
							...exams,
							rows: exams.rows.map((exam) => {
								if (exam.id === +params.id) {
									return {
										...exam,
										...newData,
									};
								}

								return exam;
							}),
						};
					},
				);
			} else {
				queryClient.removeQueries({ queryKey: [unfinishedExamsKey] });
				queryClient.removeQueries({ queryKey: [finishedExamsKey] });
				queryClient.removeQueries({ queryKey: ["courses"] });
				queryClient.invalidateQueries({ queryKey: ["exam-question"] });
			}
		},
	});
}

export function useReportQuestion() {
	return useMutation({
		mutationFn: ({ id, details }) => {
			return newApi.reportQuestion(id, details);
		},
	});
}

export const removeQueryDetailsCache = (id) => {
	return queryClient.removeQueries({ queryKey: ["exam-question", id] });
};
